"use client";
import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
const CustomCheckbox = dynamic(() => import("../../Home/Quote/QuoteForm/CustomCheckbox"));

type Option = { name: string; slug: string };

interface Props {
  id: string;
  title: string;
  isOpen: boolean;
  options: Option[];
  isChanged: boolean;
  onOpen: () => void;
  onClose: () => void;
  selectedFilters: Option[];
  OnChecked: (val: Option[]) => void;
  /** optional - if you render the dropdown in a portal, pass the portal root element id */
  portalRootId?: string;
  variant?: "blue" | "green";
}

const FilterDropdown: React.FC< Props> = ({ id,
  title,
  isOpen,
  onOpen,
  onClose,
  options,
  OnChecked,
  isChanged,
  portalRootId,
  variant = "blue",
  selectedFilters,
 }) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    setSelected(selectedFilters?.map((f) => f.slug));
  }, [isChanged, selectedFilters]);
  // toggle selection
  const toggle = (slug: string) => {
    const checked = selected.includes(slug)
      ? selected.filter((s) => s !== slug)
      : [...selected, slug];
    setSelected(checked);
    OnChecked(
      checked.map((slug) => {
        return {
          slug: slug,
          name: options.find((o) => o.slug === slug)?.name || "",
        };
      }),
    );
  };

  const clearSelection = () => {
    setSelected([]);
    OnChecked([]);
  };

  useEffect(() => {
    // handle clicks/touches outside the dropdown root (and optional portal)
    const handlePointerDown = (e: PointerEvent | MouseEvent | TouchEvent) => {
      if (!isOpen) return;

      const target = e.target as Node | null;
      if (!target) return;

      // If click is inside our dropdown root -> ignore
      if (rootRef.current?.contains(target)) return;

      // If dropdown content is rendered in a portal, also treat clicks inside portal root as inside
      if (portalRootId) {
        const portalRoot = document.getElementById(portalRootId);
        if (portalRoot && portalRoot.contains(target)) return;
      }

      // otherwise considered outside — close
      onClose();
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === "Escape") onClose();
    }

    // use pointerdown for earlier detection (works with mouse/touch/stylus)
    document.addEventListener(
      "pointerdown",
      handlePointerDown as EventListener,
    );
    document.addEventListener(
      "touchstart",
      handlePointerDown as EventListener,
      { passive: true },
    );
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener(
        "pointerdown",
        handlePointerDown as EventListener,
      );
      document.removeEventListener(
        "touchstart",
        handlePointerDown as EventListener,
      );
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose, portalRootId]);
  const strokeColor = variant === "blue" ? "#0086E6" : "#8ABC00";
  return (
    <div ref={rootRef} className="relative block">
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={`dropdown-${id}`}
        onClick={() => (isOpen ? onClose() : onOpen())}
        className="flex justify-between items-center w-full bg-white border border-border px-[17px] py-5 cursor-pointer"
      >
        <span>{title}</span>
        <figure
          className={`aspect-square w-[32px] flex justify-center items-center border ${variant === "blue" ? "bg-blue-50 border-border " : "bg-green-50 border-green-100"}`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M13.2788 5.9668L8.93208 10.3135C8.41875 10.8268 7.57875 10.8268 7.06542 10.3135L2.71875 5.9668"
              stroke={strokeColor}
              strokeWidth="1.5"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </figure>
      </button>

      {isOpen && (
        <div
          id={`dropdown-${id}`}
          role="menu"
          aria-labelledby={`dropdown-${id}`}
          className={`absolute z-50 w-full bg-white border-t-0 border shadow-lg p-3 border-border`}
        >
          <div className="p-3 border border-border flex items-center gap-2">
            <Image
              src="/images/SearchIcon.svg"
              alt="search"
              width={20}
              height={20}
            />
            <input
              type="search"
              placeholder={`Search ${title}...`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full text-sm focus-within:outline-none"
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>

          <ul className="max-h-44 overflow-auto space-y-1 mt-2.5">
            {options
              .filter((o) => o.name.toLowerCase().includes(query.toLowerCase()))
              .map((o) => (
                <li
                  key={o.slug}
                  className="hover:bg-gray-100"
                >
                  <CustomCheckbox
                    name={o.slug}
                    onChange={() => toggle(o.slug)}
                    checked={selected.includes(o.slug)}
                    label={o.name}
                    labelClass="text-subtitle-2 font-semibold text-primary-text mt-[2px]"
                    wrapperClass="p-3 w-full"
                  />
                </li>
              ))}
            {options.filter((o) =>
              o.name.toLowerCase().includes(query.toLowerCase()),
            ).length === 0 && (
              <li className="text-sm text-gray-500 px-2 py-1">No results</li>
            )}
          </ul>

          <div className="flex justify-between items-center mt-3 text-sm text-gray-600">
            <button
              onClick={clearSelection}
              className="text-sm text-red-500 cursor-pointer"
            >
              Clear
            </button>
            <span>
              {selected.length} of {options.length}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
export default FilterDropdown;
